import { GroupInvitationStatus } from 'multirpc-sdk';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { guardDialogSlice } from 'modules/guardDialog';
import { pricingRoutesConfig } from 'domains/pricing/routes';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useCommonNotificationsData } from 'modules/notifications/hooks/useCommonNotificationsData';
import { useIsXSDown } from 'uiKit/Theme/useTheme';
import { usePartnerClientStatus } from 'domains/jwtToken/hooks/usePartnerClientStatus';
import { useTeamInvitationsQuery } from 'modules/teams/actions/hooks/useTeamInvitationsQuery';

import { getSecondMenuItems } from '../utils/getSecondMenuItems';
import { getTopMenuItems } from '../utils/getTopMenuItems';

export interface IUseMenuItemsProps {
  chainsRoutes: string[];
  isMobileSideBar: boolean;
}

const invitationsStatuses = [GroupInvitationStatus.PENDING];

export const useMenuItems = ({
  chainsRoutes,
  isMobileSideBar,
}: IUseMenuItemsProps) => {
  const { isPersonalAccount } = useAccount();
  const { isEnterprise } = useAccountStatus();
  const { isLoggedIn, isWeb3Auth } = useAuth();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { isPartnerClient } = usePartnerClientStatus();

  const onOpenAccessDeniedDialog = useCallback(() => {
    dispatch(guardDialogSlice.actions.showDialog());
  }, [dispatch]);

  const { unseenNotificationsAmount } = useCommonNotificationsData();
  const { teamInvitationsAmount } = useTeamInvitationsQuery({
    invitationsStatuses,
    skipFetching: true,
  });

  const isXsDown = useIsXSDown();

  const topMenuItems = useMemo(
    () =>
      getTopMenuItems({
        currentPath: location.pathname,
        chainsRoutes,
        isEnterpriseClient: isEnterprise,
        isLoggedIn,
        isMobileSideBar,
        isXsDown,
        isPartnerClient,
        isPersonalGroup: isPersonalAccount,
        isWeb3Auth,
        notificationsAmount: unseenNotificationsAmount,
        onOpenAccessDeniedDialog,
        teamInvitationsAmount,
        onEnterpriseClick: () =>
          navigate(pricingRoutesConfig.pricing.generatePath()),
      }),
    [
      chainsRoutes,
      isEnterprise,
      isLoggedIn,
      isMobileSideBar,
      isPartnerClient,
      isPersonalAccount,
      isWeb3Auth,
      isXsDown,
      location,
      navigate,
      onOpenAccessDeniedDialog,
      teamInvitationsAmount,
      unseenNotificationsAmount,
    ],
  );

  const secondMenuItems = useMemo(
    () =>
      getSecondMenuItems({
        isLoggedIn,
        isPartnerClient,
      }),
    [isLoggedIn, isPartnerClient],
  );

  return {
    secondMenuItems,
    topMenuItems,
  };
};
